<template>
  <teleport to="body">
    <transition name="info-modal" appear>
      <div v-if="modelValue" class="modal-overlay info-modal-overlay" @click.self="onOverlayClick">
        <div ref="modalContent" class="modal-content info-modal-content alert-modal-card" :class="`alert-modal-card--${normalizedType}`" role="dialog" aria-modal="true" :aria-labelledby="labelId">
          <div class="modal-sub-header">
            <div class="sub-header">
              <ModalHeader>
                <template #default>
                  <h2 class="page-title" :id="labelId">{{ resolvedTitle }}</h2>
                </template>
              </ModalHeader>
            </div>
          </div>

          <div class="modal-body alert-modal-body">
            <div class="alert-modal-hero">
              <span class="alert-modal-icon" v-html="typeIcon"></span>
              <div class="alert-modal-message">
                <span class="alert-modal-kicker">{{ typeLabel }}</span>
                <p v-if="resolvedMessage" class="info-modal-default-text">
                  {{ resolvedMessage }}
                </p>
              </div>
            </div>

            <div v-if="alertcode" class="alert-modal-meta">
              <span class="alert-modal-meta__label">{{ trackingCodeLabel }}</span>
              <span class="alert-modal-meta__value">{{ alertcode }}</span>
            </div>
          </div>

          <div class="modal-actions alert-modal-actions">
            <BaseButton v-if="hasDownloadLink" :title="downloadErrorTitle" variant="primary" type="button" @click="downloadError" />
            <BaseButton :title="closeButtonTitle" variant="cancel" type="button" @click="close" />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, ref, watch, onUnmounted, nextTick } from 'vue';
import ModalHeader from '@/components/Common/ModalHeader.vue';
import BaseButton from '@/components/UiCompunents/BaseButton.vue';
import { getIcon } from '@/utils/icons';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  message: {
    type: [String, Number, null],
    default: '',
  },
  type: {
    type: [String, null],
    default: 'error',
  },
  title: {
    type: [String, null],
    default: '',
  },
  link: {
    type: [String, null],
    default: '',
  },
  alertcode: {
    type: [String, Number, null],
    default: '',
  },
  typeLabel: {
    type: [String, null],
    default: '',
  },
  closeOnOverlay: {
    type: Boolean,
    default: true,
  },
  id: {
    type: String,
    default: 'alert-modal',
  },
});

const emit = defineEmits(['update:modelValue', 'close']);

const modalContent = ref(null);
let openerElement = null;
const downloadErrorTitle = '\u062f\u0627\u0646\u0644\u0648\u062f \u062e\u0637\u0627';
const closeButtonTitle = '\u0628\u0633\u062a\u0646';
const trackingCodeLabel = '\u06a9\u062f \u067e\u06cc\u06af\u06cc\u0631\u06cc:';

const typeLabels = {
  error: '\u062e\u0637\u0627',
  success: '\u0645\u0648\u0641\u0642\u06cc\u062a',
  info: '\u0627\u0637\u0644\u0627\u0639 \u0631\u0633\u0627\u0646\u06cc',
  warning: '\u0647\u0634\u062f\u0627\u0631',
};

const typeIcons = {
  error: 'error',
  success: 'check',
  info: 'basicInfo',
  warning: 'error',
};

const normalizedType = computed(() => {
  const type = String(props.type || 'error').toLowerCase();
  return ['error', 'success', 'info', 'warning'].includes(type) ? type : 'info';
});

const typeLabel = computed(() => {
  const customLabel = String(props.typeLabel || '').trim();
  return customLabel || typeLabels[normalizedType.value];
});
const typeIcon = computed(() => getIcon(typeIcons[normalizedType.value]));

const typeTitles = {
  error: '\u062e\u0637\u0627\u06cc \u0633\u06cc\u0633\u062a\u0645',
  success: '\u067e\u06cc\u0627\u0645 \u0633\u06cc\u0633\u062a\u0645',
  info: '\u067e\u06cc\u0627\u0645 \u0633\u06cc\u0633\u062a\u0645',
  warning: '\u0647\u0634\u062f\u0627\u0631 \u0633\u06cc\u0633\u062a\u0645',
};

const resolvedTitle = computed(() => {
  const title = String(props.title || '').trim();
  if (title) return title;
  return typeTitles[normalizedType.value] || typeTitles.info;
});

const resolvedMessage = computed(() => {
  const message = props.message == null ? '' : String(props.message).trim();
  return message || '\u062e\u0637\u0627\u06cc \u0633\u06cc\u0633\u062a\u0645\u06cc \u0631\u062e \u062f\u0627\u062f\u0647 \u0627\u0633\u062a.';
});

const hasDownloadLink = computed(() => String(props.link || '').trim().length > 0);

function close() {
  emit('update:modelValue', false);
  emit('close');
}

function onOverlayClick() {
  if (props.closeOnOverlay) close();
}

function downloadError() {
  if (!hasDownloadLink.value) return;
  window.open(String(props.link).trim(), '_blank', 'noopener,noreferrer');
}

function handleAlertKeydown(e) {
  if (e.key === 'Escape' || e.key === 'Esc') close();
}

const focusableSelectors = 'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [contenteditable], [tabindex]:not([tabindex="-1"])';

function getFocusableElements() {
  if (!modalContent?.value) return [];
  return Array.from(modalContent.value.querySelectorAll(focusableSelectors)).filter(
    (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
  );
}

function handleTabKeydown(e) {
  if (e.key !== 'Tab') return;
  const focusable = getFocusableElements();
  if (focusable.length === 0) {
    e.preventDefault();
    modalContent.value && modalContent.value.focus();
    return;
  }

  const firstElem = focusable[0];
  const lastElem = focusable[focusable.length - 1];

  if (!e.shiftKey && document.activeElement === lastElem) {
    e.preventDefault();
    firstElem.focus();
  } else if (e.shiftKey && document.activeElement === firstElem) {
    e.preventDefault();
    lastElem.focus();
  }
}

watch(() => props.modelValue, async (val) => {
  if (val) {
    openerElement = document.activeElement;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleAlertKeydown);
    window.addEventListener('keydown', handleTabKeydown);
    await nextTick();
    const focusable = getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (modalContent.value) {
      modalContent.value.setAttribute('tabindex', '-1');
      modalContent.value.focus();
    }
  } else {
    document.body.style.overflow = 'auto';
    window.removeEventListener('keydown', handleAlertKeydown);
    window.removeEventListener('keydown', handleTabKeydown);
    try {
      if (openerElement && typeof openerElement.focus === 'function') openerElement.focus();
    } catch (e) {
      void e;
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleAlertKeydown);
  window.removeEventListener('keydown', handleTabKeydown);
  document.body.style.overflow = 'auto';
});

const labelId = `alert-modal-${props.id}`;
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(var(--text-color-secondary-rgb), 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 30000 !important;
  -webkit-backdrop-filter: saturate(180%) blur(5px) !important;
  backdrop-filter: saturate(180%) blur(5px) !important;
}

.info-modal-content {
  width: auto;
  max-width: 720px;
  min-width: 320px;
  height: auto;
  max-height: 80vh;
  border-radius: 12px;
  padding: 14px 18px 16px;
  box-shadow: 0 18px 46px rgba(var(--text-color-secondary-rgb), 0.16);
  background-color: var(--background-color);
  color: var(--text-color-primary);
}

.alert-modal-card {
  --alert-accent: var(--background-color-button);
  --alert-accent-dark: var(--background-color-button-hover);
  --alert-accent-soft: rgba(var(--background-color-button-rgb), 0.1);
  --alert-accent-surface: var(--background-color);
  --alert-accent-border: rgba(var(--background-color-button-rgb), 0.34);
  --alert-accent-line: rgba(var(--background-color-button-rgb), 0.5);
  --alert-panel-bg: var(--background-color-secondary);
  overflow: hidden;
  border: 1px solid var(--alert-accent-line);
  background:
    linear-gradient(
      180deg,
      rgba(var(--background-color-button-rgb), 0.04),
      rgba(var(--text-color-secondary-rgb), 0.03)
    ),
    var(--alert-accent-surface);
  color: var(--text-color-primary);
}

.alert-modal-card--error {
  --alert-accent: #dc2626;
  --alert-accent-dark: #991b1b;
  --alert-accent-soft: rgba(220, 38, 38, 0.08);
  --alert-accent-surface: var(--background-color);
  --alert-accent-border: rgba(153, 27, 27, 0.25);
  --alert-accent-line: rgba(153, 27, 27, 0.46);
}

.alert-modal-card--warning {
  --alert-accent: #d97706;
  --alert-accent-dark: #92400e;
  --alert-accent-soft: rgba(217, 119, 6, 0.09);
  --alert-accent-surface: var(--background-color);
  --alert-accent-border: rgba(146, 64, 14, 0.25);
  --alert-accent-line: rgba(146, 64, 14, 0.44);
}

.alert-modal-card--success {
  --alert-accent: #059669;
  --alert-accent-dark: #047857;
  --alert-accent-soft: rgba(5, 150, 105, 0.08);
  --alert-accent-surface: var(--background-color);
  --alert-accent-border: rgba(4, 120, 87, 0.25);
  --alert-accent-line: rgba(4, 120, 87, 0.44);
}

.alert-modal-body {
  padding: 14px 18px 16px !important;
}

.alert-modal-hero {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  padding: 14px 15px;
  border: 1px solid var(--alert-accent-border);
  border-radius: 8px;
  background: var(--alert-panel-bg);
  box-shadow: inset 3px 0 0 var(--alert-accent-dark);
}

.alert-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid var(--alert-accent-line);
  background: var(--alert-accent-soft);
  color: var(--alert-accent-dark);
}

.alert-modal-message {
  min-width: 0;
}

.alert-modal-kicker {
  display: block;
  margin-bottom: 6px;
  color: var(--alert-accent-dark);
  font-size: 13px;
  font-weight: 800;
}

.alert-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 18px !important;
}
.info-modal-content .modal-sub-header {
  padding: 0;
  background: transparent;
}
.info-modal-content .sub-header {
  min-height: 0;
}
.info-modal-content .modal-actions {
  background: transparent;
}
.info-modal-content .modal-body {
  padding: 6px 2px 12px 2px;
  overflow-y: auto;
  font-size: calc(0.7vw + 8px);
  direction: rtl;
  text-align: right;
}
.info-modal-default-text {
  margin: 0;
  white-space: pre-line;
}
.alert-modal-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid var(--alert-accent-border);
  border-radius: 8px;
  background: var(--alert-panel-bg);
  color: var(--text-color-secondary);
}
.alert-modal-meta__label {
  font-size: 13px;
  font-weight: 800;
  color: var(--text-color-secondary);
}
.alert-modal-meta__value {
  direction: ltr;
  unicode-bidi: plaintext;
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--alert-accent-soft);
  color: var(--alert-accent-dark);
  font-size: 13px;
  font-weight: 800;
}
.info-modal-overlay { align-items: center !important; justify-content: center !important; }

.info-modal-enter-active, .info-modal-leave-active {
  transition: opacity 220ms cubic-bezier(.2, .9, .2, 1);
}
.info-modal-enter-from, .info-modal-leave-to {
  opacity: 0;
}
.info-modal-enter-to, .info-modal-leave-from {
  opacity: 1;
}
.info-modal-enter-active .info-modal-content, .info-modal-leave-active .info-modal-content {
  transition: transform 220ms cubic-bezier(.2, .9, .2, 1), opacity 220ms linear;
}
.info-modal-enter-from .info-modal-content, .info-modal-leave-to .info-modal-content {
  transform: translateY(-10px) scale(0.98);
  opacity: 0.96;
}
.info-modal-enter-to .info-modal-content, .info-modal-leave-from .info-modal-content {
  transform: translateY(0) scale(1);
  opacity: 1;
}

.info-modal-content :deep(.modal-header) {
  direction: rtl !important;
  min-height: 0 !important;
  margin: 5px !important;
  padding: 0 !important;
  background: transparent !important;
}
.info-modal-content :deep(.modal-header) :deep(.header-right) {
  margin-left: auto !important;
  flex-direction: row-reverse !important;
  gap: 6px !important;
  align-items: center !important;
  width: auto !important;
}
.info-modal-content :deep(.modal-header) :deep(.modal-title) {
  text-align: right !important;
  width: auto !important;
  line-height: 1.35 !important;
}
.info-modal-content :deep(.modal-header .modal-title h2) {
  text-align: right !important;
  font-size: 16px !important;
  line-height: 1.35 !important;
  font-weight: 800 !important;
}
.info-modal-content :deep(.modal-header) :deep(.header-left) { display: none !important; }
.info-modal-content :deep(.modal-info-icon) { display: none !important; }

.alert-modal-card :deep(.page-title) {
  color: var(--alert-accent-dark) !important;
  margin-bottom: 10px !important;
}

.alert-modal-card :deep(.kb-btn--primary) {
  --bg: transparent;
  --bg-hover: var(--alert-accent-soft);
  --text: var(--alert-accent-dark);
  --border: var(--alert-accent-line);
  --border-hover: var(--alert-accent-dark);
}

.alert-modal-card :deep(.kb-btn--cancel) {
  --bg: var(--background-color-button2);
  --bg-hover: var(--background-color-button-hover2);
  --text: var(--text-color-thirdly);
  --border: rgba(var(--text-color-secondary-rgb), 0.22);
  --border-hover: rgba(var(--text-color-secondary-rgb), 0.34);
}

.info-modal-content .modal-body {
  scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
  scrollbar-width: thin;
}
.info-modal-content .modal-body::-webkit-scrollbar {
  width: 10px;
  background: var(--scrollbar-track);
}
.info-modal-content .modal-body::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: 8px;
}
.info-modal-content .modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--text-color-secondary-rgb), 0.18);
}

@media (max-width: 768px) {
  .info-modal-overlay {
    align-items: center !important;
    justify-content: center !important;
    padding: 10px !important;
  }

  .info-modal-content {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 480px !important;
    max-height: min(86vh, 680px) !important;
    padding: 16px 12px 12px !important;
    border-radius: 16px !important;
    display: flex;
    flex-direction: column;
  }

  .alert-modal-card::before {
    content: "";
    width: 44px;
    height: 4px;
    margin: 0 auto 10px;
    border-radius: 999px;
    background: rgba(var(--text-color-secondary-rgb), 0.28);
    flex: 0 0 auto;
  }

  .info-modal-content .modal-sub-header {
    flex: 0 0 auto;
  }

  .info-modal-content :deep(.modal-header) {
    margin: 0 2px 10px !important;
  }

  .info-modal-content :deep(.modal-header .modal-title h2) {
    font-size: 15px !important;
    line-height: 1.7 !important;
  }

  .alert-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 6px 2px 10px !important;
  }

  .alert-modal-hero {
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 10px;
    padding: 12px;
    border-radius: 10px;
    box-shadow: inset 3px 0 0 var(--alert-accent-dark);
  }

  .alert-modal-icon {
    width: 40px;
    height: 40px;
  }

  .alert-modal-icon :deep(svg) {
    width: 22px;
    height: 22px;
  }

  .alert-modal-kicker {
    margin-bottom: 4px;
    font-size: 12px;
  }

  .info-modal-default-text {
    font-size: 13px;
    line-height: 1.9;
    overflow-wrap: anywhere;
  }

  .alert-modal-meta {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
  }

  .alert-modal-meta__value {
    display: block;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    overflow-wrap: anywhere;
  }

  .alert-modal-actions {
    flex: 0 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    padding: 10px 2px 0 !important;
  }

  .alert-modal-actions :deep(.kb-btn) {
    width: 100%;
    min-height: 38px;
    margin: 0;
  }
}

@media (max-width: 420px) {
  .alert-modal-actions {
    grid-template-columns: 1fr;
  }
}
</style>
